// SPDX-License-Identifier: AGPL-3.0-or-later
import Initialization from '@app/features/app/state/Initialization';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import Channels from '@app/features/channel/state/Channels';
import type {ReadyPayload} from '@app/features/gateway/events/GatewayReady';
import GatewayConnection from '@app/features/gateway/transport/GatewayConnection';
import GuildList from '@app/features/guild/state/GuildList';
import Guilds from '@app/features/guild/state/Guilds';
import GuildMembers from '@app/features/member/state/GuildMembers';
import {ChannelMessages} from '@app/features/messaging/state/ChannelMessages';
import Messages from '@app/features/messaging/state/MessagingMessages';
import SelectedChannel from '@app/features/navigation/state/SelectedChannel';
import Permission from '@app/features/permissions/state/Permission';
import {isNativeMobileShell, NativePlugins} from '@app/features/platform/native_mobile/NativeMobileBridge';
import SessionManager from '@app/features/platform/state/AuthSession';
import {Logger} from '@app/features/platform/utils/AppLogger';
import ReadStates from '@app/features/read_state/state/ReadStates';
import Relationships from '@app/features/relationship/state/Relationships';
import Dimension from '@app/features/ui/state/Dimension';
import UserGuildSettings from '@app/features/user/state/UserGuildSettings';
import UserSettings from '@app/features/user/state/UserSettings';
import Users from '@app/features/user/state/Users';
import {MessageStates} from '@konvo/constants/src/ChannelConstants';
import {reaction, runInAction} from 'mobx';
import {
	type CachedChatPage,
	CHAT_CACHE_CHANNEL_LIMIT,
	CHAT_CACHE_MAX_AGE,
	CHAT_CACHE_MESSAGE_LIMIT,
	chatCacheDelete,
	chatCacheRead,
	chatCacheWrite,
	isFreshChatPage,
} from './ChatCache';

type BootData = Pick<
	ReadyPayload,
	| 'user'
	| 'guilds'
	| 'private_channels'
	| 'users'
	| 'relationships'
	| 'user_settings'
	| 'user_guild_settings'
	| 'read_states'
	| 'read_state_proto'
>;
interface Snapshot {
	version: 1;
	savedAt: number;
	boot: BootData;
	pages: Array<CachedChatPage>;
}
const logger = new Logger('MobileChatCache');
let boot: BootData | null = null;
let bootKey: string | null = null;
const pageSavedAt = new Map<string, number>();
let lastLiveUpdateAt = 0;

function cacheKey(): string | null {
	return SessionManager.token && SessionManager.userId
		? JSON.stringify([RuntimeConfig.apiEndpoint, SessionManager.userId])
		: null;
}

export function rememberMobileChatBoot(data: ReadyPayload): void {
	if (!isNativeMobileShell()) return;
	// Deliberately omit gateway session IDs, auth hashes, credentials and unrelated settings.
	const {
		user,
		guilds,
		private_channels,
		users,
		relationships,
		user_settings,
		user_guild_settings,
		read_states,
		read_state_proto,
	} = data;
	boot = {
		user,
		guilds,
		private_channels,
		users,
		relationships,
		user_settings,
		user_guild_settings,
		read_states,
		read_state_proto,
	};
	bootKey = cacheKey();
	queueMicrotask(persist);
}

function persist(): void {
	const key = cacheKey();
	if (!key || key !== bootKey || !boot || !Initialization.isReady) return;
	const pages: Array<CachedChatPage> = [];
	ChannelMessages.forEach((page) => {
		if (!page.ready || !page.hasNewestMessages() || !Channels.getChannel(page.channelId)) return;
		const savedAt = page.cached ? pageSavedAt.get(page.channelId) : Date.now();
		if (savedAt == null || Date.now() - savedAt >= CHAT_CACHE_MAX_AGE) return;
		pageSavedAt.set(page.channelId, savedAt);
		pages.push({
			channelId: page.channelId,
			savedAt,
			scroll: page.length <= CHAT_CACHE_MESSAGE_LIMIT ? Dimension.channelDimensionsFor(page.channelId) : undefined,
			messages: page
				.toArray()
				.filter((message) => message.state === MessageStates.SENT)
				.slice(-CHAT_CACHE_MESSAGE_LIMIT)
				.reverse()
				.map((message) => message.toJSON()),
		});
	});
	const currentBoot: BootData = {
		...boot,
		private_channels: Channels.getPrivateChannels().map((channel) => channel.toJSON()),
		guilds: boot.guilds
			.filter((guild) => guild.unavailable || Guilds.getGuild(guild.id))
			.map((guild) =>
				guild.unavailable
					? guild
					: {...guild, channels: Channels.getGuildChannels(guild.id).map((channel) => channel.toJSON())},
			),
		read_state_proto: undefined,
		read_states: ReadStates.getChannelIds().map((id) => ({
			id,
			last_message_id: ReadStates.ackMessageId(id),
			mention_count: ReadStates.getMentionCount(id),
		})),
	};
	void chatCacheWrite(key, {
		version: 1,
		savedAt: Date.now(),
		boot: currentBoot,
		pages: pages
			.sort((a, b) =>
				a.channelId === SelectedChannel.currentChannelId
					? -1
					: b.channelId === SelectedChannel.currentChannelId
						? 1
						: b.savedAt - a.savedAt,
			)
			.slice(0, CHAT_CACHE_CHANNEL_LIMIT),
	} satisfies Snapshot);
}

function applyPages(pages: Array<CachedChatPage>, fromNative = false): void {
	for (const page of pages.slice(0, CHAT_CACHE_CHANNEL_LIMIT)) {
		if (
			!isFreshChatPage(page) ||
			!Channels.getChannel(page.channelId) ||
			(fromNative && page.savedAt <= lastLiveUpdateAt)
		)
			continue;
		const existing = Messages.getCachedMessages(page.channelId);
		// Never overwrite live data, a paginated history window, or an in-flight request.
		if (existing?.loadingMore) continue;
		if (existing && existing.length > 0) {
			if (!fromNative || page.savedAt <= (pageSavedAt.get(page.channelId) ?? Infinity)) continue;
			if (!existing.cached && (!existing.hasNewestMessages() || !Dimension.channelPinnedToEnd(page.channelId)))
				continue;
		}
		if (
			!fromNative &&
			page.scroll &&
			[page.scroll.scrollTop, page.scroll.scrollHeight, page.scroll.offsetHeight].every(
				(value) => Number.isFinite(value) && value >= 0,
			)
		) {
			Dimension.recordChannelDimensions(
				page.channelId,
				page.scroll.scrollTop,
				page.scroll.scrollHeight,
				page.scroll.offsetHeight,
			);
		}
		pageSavedAt.set(page.channelId, page.savedAt);
		Messages.handleLoadMessagesSuccess({
			channelId: page.channelId,
			messages: page.messages,
			cached: true,
			hasMoreBefore: true,
		});
		ReadStates.handleLoadMessages({channelId: page.channelId, messages: page.messages});
	}
}

export async function restoreNativeMessageCache(): Promise<void> {
	const key = cacheKey();
	const plugin = NativePlugins.native();
	if (!key || !plugin?.getCachedMessages) return;
	try {
		const version = Messages.version;
		const apiBase = new URL(
			`${RuntimeConfig.apiEndpoint.replace(/\/$/, '')}/v${RuntimeConfig.apiCodeVersion}`,
			window.location.origin,
		)
			.toString()
			.replace(/\/$/, '');
		const result = await plugin.getCachedMessages({userId: SessionManager.userId!, apiBase});
		if (
			key !== cacheKey() ||
			GatewayConnection.isConnected ||
			Messages.version !== version ||
			!Array.isArray(result.pages)
		)
			return;
		runInAction(() => applyPages(result.pages, true));
	} catch {
		/* Older native builds do not implement this optional bridge method. */
	}
}

async function restore(): Promise<void> {
	const key = cacheKey();
	if (!key) return;
	const snapshot = await chatCacheRead<Snapshot>(key);
	if (
		key !== cacheKey() ||
		Initialization.hasCompletedInitialLoad ||
		!snapshot ||
		snapshot.version !== 1 ||
		snapshot.boot?.user?.id !== SessionManager.userId ||
		!Array.isArray(snapshot.pages) ||
		!Number.isFinite(snapshot.savedAt) ||
		snapshot.savedAt > Date.now() ||
		Date.now() - snapshot.savedAt >= CHAT_CACHE_MAX_AGE
	)
		return;
	runInAction(() => {
		const data = snapshot.boot;
		Users.cacheUsers([data.user, ...(data.users ?? [])]);
		Relationships.loadRelationships(data.relationships ?? []);
		Guilds.handleGatewayReady({guilds: data.guilds});
		GuildList.handleGatewayReady(data.guilds);
		GuildMembers.handleGatewayReady(data.guilds);
		const channels = [
			...(data.private_channels ?? []),
			...data.guilds.flatMap((guild) =>
				guild.unavailable ? [] : guild.channels.map((channel) => ({...channel, guild_id: guild.id})),
			),
		];
		Channels.handleGatewayReady({channels});
		UserSettings.handleGatewayReady(data.user_settings);
		UserGuildSettings.handleGatewayReady(data.user_guild_settings ?? []);
		Permission.handleGatewayReady();
		ReadStates.handleGatewayReady({readState: data.read_states ?? [], readStateProto: data.read_state_proto, channels});
		applyPages(snapshot.pages);
		Initialization.setCachedContentAvailable();
	});
	await restoreNativeMessageCache();
}

export function startMobileChatCache(): () => void {
	if (!isNativeMobileShell()) return () => {};
	let timer: ReturnType<typeof setTimeout> | undefined;
	const schedule = () => {
		if (GatewayConnection.isConnected) lastLiveUpdateAt = Date.now();
		clearTimeout(timer);
		timer = setTimeout(persist, 500);
	};
	const stopMessages = Messages.subscribe(schedule);
	const stopSession = reaction(
		cacheKey,
		(key, previous) => {
			clearTimeout(timer);
			if (bootKey !== key) {
				boot = null;
				bootKey = null;
			}
			if (previous !== undefined && previous !== key) {
				pageSavedAt.clear();
				lastLiveUpdateAt = 0;
			}
			if (previous && previous !== key) void chatCacheDelete(previous);
			void restore().catch((error) => logger.warn('Unable to restore saved chats', error));
		},
		{fireImmediately: true},
	);
	const visibility = () => {
		if (document.visibilityState === 'hidden') persist();
		else void restoreNativeMessageCache();
	};
	document.addEventListener('visibilitychange', visibility);
	window.addEventListener('pagehide', persist);
	return () => {
		clearTimeout(timer);
		stopMessages();
		stopSession();
		document.removeEventListener('visibilitychange', visibility);
		window.removeEventListener('pagehide', persist);
	};
}
